/**
 * Lazy Amplify bootstrap. Imported only from /host/* route chunks so
 * Cognito + the Amplify runtime never lands in the player or kiosk
 * bundles.
 *
 * Config comes from the runtime `/config.json` loaded by
 * `lib/runtime-config.ts` at SPA boot. When the user pool fields are
 * empty (Floci / dev), `isCognitoConfigured()` returns false and the
 * host shell stays on the existing `x-user-sub` / `x-user-email`
 * header fallback — the auth UI never renders.
 */
import { getRuntimeConfig } from './runtime-config';

let configured = false;

/**
 * True when the Cognito user pool + client are wired via the runtime
 * config. When false the host routes skip the auth UI entirely and
 * rely on the dev header fallback (api-context.tsx). This is how
 * Floci stays usable without a deployed user pool.
 */
export function isCognitoConfigured(): boolean {
  const cfg = getRuntimeConfig();
  return !!cfg.cognitoUserPoolId && !!cfg.cognitoUserPoolClientId;
}

export async function ensureAmplify(): Promise<void> {
  if (configured) return;
  if (!isCognitoConfigured()) {
    configured = true;
    return;
  }
  const cfg = getRuntimeConfig();
  const { Amplify } = await import('aws-amplify');
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cfg.cognitoUserPoolId,
        userPoolClientId: cfg.cognitoUserPoolClientId,
        loginWith: { email: true },
        signUpVerificationMethod: 'code',
      },
    },
  });
  configured = true;
}

/**
 * Current Cognito ID token, or null if the host isn't signed in
 * (or Cognito isn't configured). Used by the api-context to attach
 * Authorization headers per-request.
 */
export async function getIdToken(): Promise<string | null> {
  if (!isCognitoConfigured()) return null;
  await ensureAmplify();
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function signOutHost(): Promise<void> {
  if (!isCognitoConfigured()) return;
  await ensureAmplify();
  const { signOut } = await import('aws-amplify/auth');
  await signOut();
}

/**
 * Whether a Cognito user is currently signed in. Used by the route
 * guard to decide between rendering the host shell vs the sign-in UI.
 */
export async function isHostSignedIn(): Promise<boolean> {
  if (!isCognitoConfigured()) return true; // dev fallback acts as signed-in
  await ensureAmplify();
  try {
    const { getCurrentUser } = await import('aws-amplify/auth');
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}
